import type { LocationExactnessScope } from "./constants";
import { getPlaceExactnessOptions } from "./getPlaceExactnessOptions";
import type { IdHierarchy, PlaceLevel } from "./types";
import { PLACE_LEVEL } from "./types";

/**
 * Computes the default location exactness for a place based on its level and hierarchy.
 *
 * Rules (from EGE-8089):
 * - Continent / CountryRegion / Country / StateRegion → null (Broad)
 * - State → State and Adjacent States
 * - CountyRegion / County → State; if no State option available (e.g. UK/Ireland) → Country
 * - City / Township / PostalCode → State; if no State option available → Country
 *
 * Uses `getPlaceExactnessOptions` internally so the returned value is always a valid
 * selectable option after exclusions and renumbering.
 */
export function getDefaultPlaceExactness(
    level: PlaceLevel,
    idHierarchy: IdHierarchy = [],
): LocationExactnessScope | null {
    // Continent / CountryRegion / Country / StateRegion → Broad
    if (level <= PLACE_LEVEL.StateRegion) {
        return null;
    }

    const options = getPlaceExactnessOptions(level, idHierarchy);
    if (options.length === 0) return null;

    // State → State and Adjacent States
    if (level === PLACE_LEVEL.State) {
        const stateAdjacent = options.find((opt) => opt.level === PLACE_LEVEL.State && opt.adjacent);
        return stateAdjacent?.value ?? null;
    }

    // County / City / Township / PostalCode → State (non-adjacent); fallback → Country.
    // Unrecognised PlaceLevels (e.g. future geo additions) also reach here and fall
    // through to the same State → Country → null resolution.
    const state = options.find((opt) => opt.level === PLACE_LEVEL.State && !opt.adjacent);
    if (state) return state.value;

    const country = options.find((opt) => opt.level === PLACE_LEVEL.Country && !opt.adjacent);
    return country?.value ?? null;
}
